import { useState } from 'react';
import { useLanguage } from '@/lib/i18n';
import { BookOpen, Lightbulb, AlertTriangle, CheckCircle2, ChevronRight, X } from 'lucide-react';
import { getHelp } from './helpContent';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

/**
 * <PageHelp pageKey="mission-control" />
 *
 * Subtle, professional in-app guide trigger.
 * Drops into any page header. Opens a side sheet with sections, cost notes, and FAQs.
 */
export default function PageHelp({ pageKey, className = '' }) {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const t = (s) => (typeof s === 'string' ? s : (ja ? s?.ja : s?.en) || '');
  const help = getHelp(pageKey);
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid={`page-help-trigger-${pageKey}`}
          aria-label={ja ? 'ページガイドを開く' : 'Open page guide'}
          className={`inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-emerald-400 transition-colors px-2 py-1 rounded-md hover:bg-slate-800/60 ${className}`}
        >
          <BookOpen className="w-3.5 h-3.5" strokeWidth={1.75} />
          <span className="hidden sm:inline">{ja ? 'ガイド' : 'Guide'}</span>
        </button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto bg-slate-950 border-l border-slate-800 text-slate-100"
        data-testid={`page-help-sheet-${pageKey}`}
      >
        <SheetHeader className="pb-2 border-b border-slate-800">
          <SheetTitle className="text-emerald-400 text-lg font-semibold flex items-center gap-2">
            <BookOpen className="w-5 h-5" strokeWidth={1.75} />
            {t(help.title)}
          </SheetTitle>
          <SheetDescription className="text-slate-400 text-xs leading-relaxed">
            {t(help.subtitle)}
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="sections" className="mt-4">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="sections" data-testid="help-tab-sections" className="text-xs">
              {ja ? '使い方' : 'How to use'}
            </TabsTrigger>
            {help.costNotes && (
              <TabsTrigger value="cost" data-testid="help-tab-cost" className="text-xs">
                {ja ? 'コスト管理' : 'Cost control'}
              </TabsTrigger>
            )}
            {help.faqs?.length > 0 && (
              <TabsTrigger value="faqs" data-testid="help-tab-faqs" className="text-xs">
                {ja ? 'よくある質問' : 'FAQs'}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Sections */}
          <TabsContent value="sections" className="space-y-5 mt-4">
            {help.sections?.map((s, idx) => (
              <section key={idx} data-testid={`help-section-${idx}`} className="space-y-2">
                <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-emerald-500" />
                  {t(s.heading)}
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed pl-6">{t(s.body)}</p>
                {s.bullets?.length > 0 && (
                  <ul className="pl-6 space-y-1.5">
                    {s.bullets.map((b, i) => (
                      <li key={i} className="text-xs text-slate-400 flex gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{t(b)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.tip && (
                  <div className="ml-6 mt-2 p-2 rounded border border-emerald-900 bg-emerald-950/40 text-xs text-emerald-200 flex gap-2">
                    <Lightbulb className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>{t(s.tip)}</span>
                  </div>
                )}
                {s.warn && (
                  <div className="ml-6 mt-2 p-2 rounded border border-amber-900 bg-amber-950/40 text-xs text-amber-200 flex gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>{t(s.warn)}</span>
                  </div>
                )}
              </section>
            ))}
          </TabsContent>

          {/* Cost notes */}
          {help.costNotes && (
            <TabsContent value="cost" className="mt-4">
              <div className="border border-slate-800 rounded p-3 bg-slate-900/50">
                <h3 className="text-sm font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />
                  {ja ? 'コスト管理のポイント' : 'Cost-control essentials'}
                </h3>
                <ul className="space-y-2">
                  {(ja ? help.costNotes.ja : help.costNotes.en).map((note, i) => (
                    <li key={i} className="text-xs text-slate-300 flex gap-2">
                      <span className="text-emerald-500 font-bold">·</span>
                      <span>{note}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </TabsContent>
          )}

          {/* FAQs */}
          {help.faqs?.length > 0 && (
            <TabsContent value="faqs" className="space-y-3 mt-4">
              {help.faqs.map((f, i) => (
                <details
                  key={i}
                  className="border border-slate-800 rounded bg-slate-900/40 px-3 py-2"
                  data-testid={`help-faq-${i}`}
                >
                  <summary className="text-xs font-semibold text-slate-100 cursor-pointer">
                    {t(f.q)}
                  </summary>
                  <p className="text-xs text-slate-400 mt-2 leading-relaxed">{t(f.a)}</p>
                </details>
              ))}
            </TabsContent>
          )}
        </Tabs>

        <div className="mt-6 pt-4 border-t border-slate-800 text-[10px] text-slate-500">
          {ja
            ? '完全な技術ドキュメント: /app/memory/HOST_PROGRAMMER_GUIDE.md'
            : 'Full technical documentation: /app/memory/HOST_PROGRAMMER_GUIDE.md'}
        </div>
      </SheetContent>
    </Sheet>
  );
}
